import { useEffect } from "react";
import { useLocation, useParams } from "wouter";
import { useAcceptInvite, useAuthMe, useInvitePreview } from "@vorloop/api-client-react";

/**
 * Accept-invite page. Routed at /accept/:token. Lifecycle:
 *   1. Fetch the public invite preview (no auth needed).
 *   2. If the visitor isn't signed in → bounce them to /signin with
 *      redirectTo set to come back here.
 *   3. If signed in → "Accept" button posts to /invites/:token/accept,
 *      then sends them into the project editor.
 */
export function AcceptInvite() {
  const params = useParams<{ token: string }>();
  const token = params.token;
  const [, setLocation] = useLocation();

  const me = useAuthMe();
  const preview = useInvitePreview(token ?? null);
  const accept = useAcceptInvite({
    onSuccess: (r) => {
      setLocation(`/editor/${r.projectId}`);
    },
  });

  // Stash the invite URL so SignIn can bounce the user back after auth.
  useEffect(() => {
    if (!token) return;
    try {
      sessionStorage.setItem("vp.afterSignin", `/accept/${token}`);
    } catch {
      // ignore
    }
  }, [token]);

  if (!token) {
    return <Splash error>Invalid invite link.</Splash>;
  }

  if (preview.isLoading || me.isLoading) {
    return <Splash>Loading invite…</Splash>;
  }

  if (preview.error) {
    return <Splash error>{preview.error.message}</Splash>;
  }

  if (!preview.data) {
    return <Splash error>Invite not found.</Splash>;
  }

  const isSignedIn = !!me.data?.user;
  const expired = preview.data.expired;
  const alreadyAccepted = preview.data.accepted;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg-canvas)",
        color: "var(--text-primary)",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "min(420px, 92vw)",
          background: "var(--bg-panel)",
          border: "1px solid var(--border-strong)",
          borderRadius: 14,
          padding: 28,
          display: "flex",
          flexDirection: "column",
          gap: 18,
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
        }}
      >
        <header style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span
            aria-hidden
            style={{
              width: 32,
              height: 32,
              borderRadius: 9,
              background: "linear-gradient(135deg, #8B5CF6, #6366F1)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            ✦
          </span>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
            You've been invited
          </h1>
          <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.5 }}>
            <strong style={{ color: "var(--text-primary)" }}>@{preview.data.ownerHandle}</strong>{" "}
            added you as a{" "}
            <strong style={{ color: "var(--text-primary)" }}>{preview.data.role}</strong>{" "}
            on{" "}
            <strong style={{ color: "var(--text-primary)" }}>{preview.data.projectName}</strong>.
          </p>
        </header>

        {expired ? (
          <Banner>This invite expired. Ask the owner to send a new one.</Banner>
        ) : alreadyAccepted ? (
          <Banner>This invite was already accepted.</Banner>
        ) : isSignedIn ? (
          <>
            {accept.error && (
              <div
                style={{
                  padding: 10,
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 8,
                  color: "var(--accent-red)",
                  fontSize: 12,
                }}
              >
                {accept.error.message}
              </div>
            )}
            <button
              type="button"
              onClick={() => accept.mutate({ token })}
              disabled={accept.isPending}
              style={{
                padding: "10px 14px",
                background: accept.isPending ? "var(--bg-active)" : "var(--accent-blue)",
                color: accept.isPending ? "var(--text-tertiary)" : "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                cursor: accept.isPending ? "wait" : "pointer",
              }}
            >
              {accept.isPending ? "Accepting…" : `Open ${preview.data.projectName}`}
            </button>
            <p style={{ margin: 0, color: "var(--text-tertiary)", fontSize: 11 }}>
              Signed in as {me.data?.user?.email}. Wrong account?{" "}
              <a href="/signin" style={{ color: "var(--accent-blue)" }}>
                Switch
              </a>
              .
            </p>
          </>
        ) : (
          <>
            <p style={{ margin: 0, color: "var(--text-tertiary)", fontSize: 12 }}>
              Sign in to accept the invite. The invite is sent to{" "}
              <strong style={{ color: "var(--text-primary)" }}>{preview.data.email}</strong>;
              you can sign in with any account.
            </p>
            <button
              type="button"
              onClick={() => setLocation("/signin")}
              style={{
                padding: "10px 14px",
                background: "var(--accent-blue)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 12,
        background: "rgba(245,158,11,0.08)",
        border: "1px solid rgba(245,158,11,0.3)",
        borderRadius: 8,
        color: "var(--accent-amber)",
        fontSize: 12,
      }}
    >
      {children}
    </div>
  );
}

function Splash({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        height: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg-canvas)",
        color: error ? "var(--accent-red)" : "var(--text-tertiary)",
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
